/**
 * EmployeeListEmbedPage — 社員一覧（社員の個人レポート参照・社長向け）をモバイルWebView用に提供。
 *
 * ルート: /embed/report/employees（EmbeddedShell 配下）。
 * - GET /analysis/company/results から自社全社員の最新診断を取得して一覧表示。
 * - **氏名検索（クライアント側フィルタ）** を Web 側に持つ（＝検索仕様は今後 Web 変更のみで調整可・審査不要）。
 * - 行タップ: モバイル WebView 内では親(native)へ postMessage(NAVIGATE) して
 *   ネイティブルート /report/employee/:userId へ遷移させ、戻る操作をネイティブに委ねる。
 *   単体 Web の場合は web 内 navigate。
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { analysisApi } from '../api/analysisApi';

interface Row {
  userId: string;
  name: string;
  patternName: string | null;
  date: string;
}

const fmtDate = (s?: string | null): string => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
};

const wrap: React.CSSProperties = {
  fontFamily: '"Hiragino Sans", "Hiragino Kaku Gothic ProN", "Yu Gothic", Meiryo, sans-serif',
  padding: 16,
  background: '#f9fafb',
  minHeight: '100vh',
  boxSizing: 'border-box',
};

export const EmployeeListEmbedPage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await analysisApi.getCompanyResults();
        const list: Row[] = (res.results ?? []).map((r) => {
          const rr = r as unknown as {
            userId: string;
            userName?: string | null;
            patternName?: string | null;
            createdAt?: string | null;
          };
          return {
            userId: rr.userId,
            name: rr.userName && rr.userName.length > 0 ? rr.userName : rr.userId,
            patternName: rr.patternName ?? null,
            date: fmtDate(rr.createdAt),
          };
        });
        if (!cancelled) {
          setRows(list);
          setState('ready');
        }
      } catch {
        if (!cancelled) setState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const open = useCallback(
    (userId: string) => {
      const path = `/report/employee/${encodeURIComponent(userId)}`;
      // モバイル WebView 内(iframe): 親(native)へ通知しネイティブ遷移。単体 Web: 直接遷移。
      if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
        window.parent.postMessage({ type: 'NAVIGATE', path }, '*');
      } else {
        navigate(path);
      }
    },
    [navigate]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? rows.filter((r) => r.name.toLowerCase().includes(q)) : rows;
  }, [rows, query]);

  if (state === 'loading') {
    return <div style={{ ...wrap, color: '#888', fontSize: 14 }}>読み込み中…</div>;
  }
  if (state === 'error') {
    return <div style={{ ...wrap, color: '#B33A3A', fontSize: 14 }}>社員一覧を取得できませんでした。</div>;
  }

  return (
    <div style={wrap}>
      <input
        type="search"
        inputMode="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="氏名で検索"
        aria-label="氏名で検索"
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '12px 14px',
          fontSize: 15,
          border: '1px solid #e5e7eb',
          borderRadius: 10,
          marginBottom: 12,
          background: '#fff',
        }}
      />
      {rows.length === 0 ? (
        <div style={{ color: '#666', fontSize: 14, textAlign: 'center', padding: '24px 0' }}>
          表示できる社員がいません。
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ color: '#666', fontSize: 14, textAlign: 'center', padding: '24px 0' }}>
          該当する社員がいません。
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
          {filtered.map((emp) => (
            <li key={emp.userId}>
              <button
                type="button"
                onClick={() => open(emp.userId)}
                aria-label={`${emp.name}の個人レポートを開く`}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: 16,
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  minHeight: 44,
                  boxSizing: 'border-box',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: '#111827',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {emp.name}
                  </div>
                  <div style={{ fontSize: 13, color: '#4b5563', marginTop: 2 }}>
                    {emp.patternName ?? '未判定'}
                    {emp.date ? `・${emp.date}` : ''}
                  </div>
                </div>
                <span style={{ fontSize: 20, color: '#9ca3af', flexShrink: 0 }}>›</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default EmployeeListEmbedPage;
